import React from 'react';
import {
  EntityPreference,
  NotificationCategory,
  NotificationPreferences,
  clearEntityPreference,
  getPreferences,
  setPreferences,
} from '../../../services/notifications/api';

/**
 * Notification settings (D-39) — the three global categories, plus whatever
 * the user has muted individually.
 *
 * Saves IMMEDIATELY on toggle rather than joining the page's Save button. A
 * notification switch is the kind of control people flip in anger ("stop
 * emailing me"), and making that wait on a save they might not notice is how
 * you keep sending notifications someone already turned off. The endpoint takes
 * PATCH semantics, so one toggle sends one field.
 */

const CATEGORY_COPY: Record<NotificationCategory, { title: string; description: string }> = {
  myPosts: {
    title: 'Likes and comments on my posts',
    description: 'When someone reacts to or comments on something you posted.',
  },
  myTrips: {
    title: 'Likes and comments on my trips',
    description: 'When someone reacts to or comments on a trip you own.',
  },
  friendsActivity: {
    title: 'Activity from people I follow',
    description: 'When someone you follow publishes a new post or trip.',
  },
  connections: {
    title: 'Follows and requests',
    // Says where the requests still are, so turning this off doesn't read as
    // "I will stop receiving follow requests" — it only silences the alert.
    description:
      'When someone follows you or asks to follow you. '
      + 'Requests still appear on your connections page.',
  },
};

const CATEGORY_ORDER: NotificationCategory[] = [
  'myPosts',
  'myTrips',
  'friendsActivity',
  'connections',
];

const NotificationSettings: React.FC = () => {
  const [preferences, setPreferencesState] = React.useState<NotificationPreferences | null>(null);
  const [entities, setEntities] = React.useState<EntityPreference[]>([]);
  const [error, setError] = React.useState<string | null>(null);
  const [unavailable, setUnavailable] = React.useState(false);

  React.useEffect(() => {
    let cancelled = false;
    getPreferences()
      .then((data) => {
        if (cancelled) return;
        setPreferencesState(data.preferences);
        setEntities(data.entities);
      })
      .catch((err: any) => {
        if (cancelled) return;
        // 503 = notifications aren't wired up in this environment. Hide the
        // whole card rather than showing switches that silently do nothing.
        if (err?.status === 503) setUnavailable(true);
        else setError('Could not load notification settings.');
      });
    return () => { cancelled = true; };
  }, []);

  const toggle = async (category: NotificationCategory, value: boolean) => {
    if (!preferences) return;
    const previous = preferences;
    setPreferencesState({ ...preferences, [category]: value }); // optimistic
    setError(null);
    try {
      setPreferencesState(await setPreferences({ [category]: value }));
    } catch {
      setPreferencesState(previous);
      setError('Could not save that change.');
    }
  };

  const unmute = async (entity: EntityPreference) => {
    const previous = entities;
    setEntities((current) => current.filter(
      (item) => !(item.entityType === entity.entityType && item.entityId === entity.entityId),
    ));
    try {
      await clearEntityPreference(entity.entityType, entity.entityId);
    } catch {
      setEntities(previous);
      setError('Could not unmute that.');
    }
  };

  if (unavailable) return null;

  // Only MUTED overrides are worth listing: an override pinned to `false`
  // (notify me even though the category is off) isn't something to "undo".
  const muted = entities.filter((entity) => entity.muted);

  return (
    <section className="v2-settings-card" aria-labelledby="notifications-heading">
      <h2 id="notifications-heading">Notifications</h2>
      <div className="v2-settings-card__body">
        {error && <p className="v2-settings-form__error">{error}</p>}

        {!preferences ? (
          <p>Loading…</p>
        ) : (
          CATEGORY_ORDER.map((category) => (
            <div className="v2-settings-toggle" key={category}>
              <div>
                <h3>{CATEGORY_COPY[category].title}</h3>
                <p>{CATEGORY_COPY[category].description}</p>
              </div>
              <button
                type="button"
                className={preferences[category] ? 'v2-settings-toggle__switch--on' : undefined}
                onClick={() => { void toggle(category, !preferences[category]); }}
                aria-pressed={preferences[category]}
                aria-label={CATEGORY_COPY[category].title}
              >
                <span />
              </button>
            </div>
          ))
        )}

        {muted.length > 0 && (
          <div className="v2-settings-muted">
            <h3>Muted</h3>
            <p>These override the switches above until you unmute them.</p>
            <ul>
              {muted.map((entity) => (
                <li key={`${entity.entityType}:${entity.entityId}`}>
                  <span>
                    {entity.entityType === 'trip' ? 'Trip' : 'Person'} · {entity.entityId}
                  </span>
                  <button type="button" onClick={() => { void unmute(entity); }}>Unmute</button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </section>
  );
};

export default NotificationSettings;
